import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../hooks/useTheme';
import MouseIcon from '@mui/icons-material/Mouse';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TitleIcon from '@mui/icons-material/Title';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ImageIcon from '@mui/icons-material/Image';
import CodeIcon from '@mui/icons-material/Code';
import SaveIcon from '@mui/icons-material/Save';
import { ShapeIcon, SHAPE_GROUPS } from '../../types/shapeGroups';

// Тулбар для свободного режима (без кодогенерации)
const ToolbarFree = ({ tool, setTool, zoomIn, zoomOut, fitToBoard, centerToSelection, elements, onExportImage, showCodePanel, setShowCodePanel, onSaveDiagram, onBeforeNavigate }) => {
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const [showMore, setShowMore] = useState(false);
  const moreButtonRef = useRef(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportButtonRef = useRef(null);

  const activeShape = Object.values(SHAPE_GROUPS).flat().find(s => s.type === tool);

  // Закрытие по клику вне
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Проверяем клик вне всего контейнера (кнопка + меню)
      if (moreButtonRef.current && !moreButtonRef.current.contains(event.target)) {
        setShowMore(false);
      }
      if (exportButtonRef.current && !exportButtonRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const iconStyle = { width: 18, height: 18 };
  const guardedNavigate = (target) => {
    if (typeof onBeforeNavigate === 'function' && !onBeforeNavigate()) return;
    navigate(target);
  };

  const Btn = ({ active, title, onClick, children, refProp }) => (
    <button
      ref={refProp}
      title={title}
      onClick={onClick}
      style={{
        backgroundColor: active ? theme.toolbar.buttonBackgroundActive : theme.toolbar.buttonBackground,
        color: theme.toolbar.buttonText,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: theme.toolbar.buttonBorder,
        borderRadius: 6,
        padding: '6px 8px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative'
      }}
    >
      {children}
    </button>
  );

  const is = (t) => tool === t;

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: theme.toolbar.background,
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: theme.toolbar.border,
        padding: '0 12px',
        zIndex: 2000,
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Btn title="Назад" onClick={() => guardedNavigate(-1)}>
          <ArrowBackIcon style={iconStyle} />
        </Btn>
        <div style={{ fontSize: 18, fontWeight: 'bold', color: theme.toolbar.text, marginRight: 8 }}>
          Свободная доска
        </div>
        <div style={{ width: 1, height: 24, backgroundColor: theme.toolbar.border }} />
        {onSaveDiagram && (
          <Btn title="Сохранить (Ctrl+S)" onClick={onSaveDiagram}>
            <SaveIcon style={iconStyle} />
          </Btn>
        )}
        <div style={{ width: 1, height: 24, backgroundColor: theme.toolbar.border }} />
        <Btn title="Выбор (S)" active={is('select')} onClick={() => setTool('select')}>
          <MouseIcon style={iconStyle} />
        </Btn>
        <Btn title="Прямоугольник (R)" active={is('rectangle')} onClick={() => setTool('rectangle')}>
          <CropSquareIcon style={iconStyle} />
        </Btn>
        <Btn title="Круг (C)" active={is('circle')} onClick={() => setTool('circle')}>
          <RadioButtonUncheckedIcon style={iconStyle} />
        </Btn>
        <Btn title="Стрелка (A)" active={is('arrow')} onClick={() => setTool('arrow')}>
          <ArrowForwardIcon style={iconStyle} />
        </Btn>
        <Btn title="Текст (T)" active={is('text')} onClick={() => setTool('text')}>
          <TitleIcon style={iconStyle} />
        </Btn>

        {onExportImage && (
            <div style={{ position: 'relative' }} ref={exportButtonRef}>
                <Btn
                    title="Сохранить как изображение"
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    active={showExportMenu}
                >
                    <ImageIcon style={iconStyle} />
                </Btn>
                {showExportMenu && (
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: 4,
                        background: theme.toolbar.background,
                        border: `1px solid ${theme.toolbar.border}`,
                        borderRadius: 6,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 3000,
                        minWidth: 100,
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '4px 0'
                    }}>
                        {['png', 'jpg', 'svg'].map(format => (
                            <button
                                key={format}
                                onClick={() => {
                                    onExportImage(format);
                                    setShowExportMenu(false);
                                }}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    color: theme.toolbar.buttonText,
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    textTransform: 'uppercase'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = theme.name === 'light' ? '#f1f5f9' : '#374151'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                                {format}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        )}

        <div ref={moreButtonRef} style={{ position: 'relative' }}>
            <Btn
                title="Больше фигур"
                active={showMore || !!activeShape}
                onClick={() => setShowMore(!showMore)}
            >
              {activeShape ? <ShapeIcon type={activeShape.type} color={theme.toolbar.buttonText} /> : <MoreHorizIcon style={iconStyle} />}
              <span style={{ marginLeft: 4, fontSize: 13 }}>{activeShape ? activeShape.label : "Больше"}</span>
            </Btn>

            {showMore && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: 8,
                    background: theme.contextMenu.background,
                    border: `1px solid ${theme.contextMenu.border}`,
                    borderRadius: 6,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    padding: 12,
                    zIndex: 2100,
                    width: 320,
                    maxHeight: 'calc(100vh - 100px)',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12
                }}>
                    {Object.entries(SHAPE_GROUPS).map(([groupName, shapes]) => (
                        <div key={groupName}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: theme.textSecondary, marginBottom: 8, paddingLeft: 4 }}>
                                {groupName}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                                {shapes.map(shape => (
                                    <button
                                        key={shape.type}
                                        onClick={() => { setTool(shape.type); setShowMore(false); }}
                                        title={shape.label}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: 8,
                                            backgroundColor: tool === shape.type ? theme.toolbar.buttonBackgroundActive : 'transparent',
                                            borderWidth: '1px',
                                            borderStyle: 'solid',
                                            borderColor: tool === shape.type ? theme.primary : 'transparent',
                                            borderRadius: 4,
                                            cursor: 'pointer',
                                            fontSize: 11,
                                            color: theme.text,
                                            gap: 6,
                                            height: 60
                                        }}
                                        onMouseEnter={e => { if(tool !== shape.type) e.currentTarget.style.backgroundColor = theme.hover; }}
                                        onMouseLeave={e => { if(tool !== shape.type) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                    >
                                        <ShapeIcon type={shape.type} color={theme.text || '#000'} />
                                        <span style={{ textAlign: 'center', lineHeight: 1.1 }}>{shape.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Btn title="Увеличить (+)" onClick={zoomIn}>
          <ZoomInIcon style={iconStyle} />
        </Btn>
        <Btn title="Уменьшить (-)" onClick={zoomOut}>
          <ZoomOutIcon style={iconStyle} />
        </Btn>
        <Btn title="Вместить (0)" onClick={fitToBoard}>
          <CenterFocusStrongIcon style={iconStyle} />
        </Btn>
        <Btn title="К выделенному" onClick={centerToSelection}>
          <GpsFixedIcon style={iconStyle} />
        </Btn>
        {typeof setShowCodePanel === 'function' && (
          <Btn
            title="Панель кода"
            active={showCodePanel}
            onClick={() => setShowCodePanel(v => !v)}
          >
            <CodeIcon style={iconStyle} />
          </Btn>
        )}
        <div style={{ width: 1, height: 24, backgroundColor: theme.toolbar.border }} />
        <Btn title="Сменить тему" onClick={toggleTheme}>
          {isDark ? <LightModeIcon style={iconStyle} /> : <DarkModeIcon style={iconStyle} />}
        </Btn>
        {elements?.length > 0 && (
          <div style={{ color: theme.toolbar.text, fontSize: 12, opacity: 0.7 }}>
            {elements.length} эл.
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolbarFree;
